import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';

const ACTIVITY_UPDATE_INTERVAL = 30000; // 30s
const INACTIVITY_TIMEOUT = 300000; // 5 minutes
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'] as const;

interface SessionState {
  sessionId: string | null;
  lastActivityTime: number;
  activityTimer: number | null;
  inactivityTimer: number | null;
}

/**
 * Hook to track student activity sessions
 * - Creates/resumes a session when the student starts using the app
 * - Updates last_activity_at periodically and on route change
 * - Tracks first_path and last_path
 * - Ends session on inactivity, tab hide, or unmount
 */
export function useStudentSession(enabled = true) {
  const location = useLocation();

  const stateRef = useRef<SessionState>({
    sessionId: null,
    lastActivityTime: Date.now(),
    activityTimer: null,
    inactivityTimer: null,
  });

  // Check if user is a student; return student id or null
  const checkIsStudent = useCallback(async (): Promise<string | null> => {
    const { data } = await supabase.auth.getUser();
    const user = data?.user;
    if (!user) return null;

    const { data: studentRow, error } = await supabase
      .from('students')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error checking student row:', error);
      return null;
    }

    return studentRow ? studentRow.id : null;
  }, []);

  // Start a new session
  const startSession = useCallback(
    async (studentId: string, firstPath: string): Promise<string | null> => {
      try {
        const nowIso = new Date().toISOString();

        const { data, error } = await supabase
          .from('student_sessions')
          .insert({
            student_user_id: studentId,
            started_at: nowIso,
            last_activity_at: nowIso,
            first_path: firstPath,
            last_path: firstPath,
          })
          .select('id')
          .single();

        if (error) {
          console.error('Error starting session:', error);
          return null;
        }

        return data?.id ?? null;
      } catch (err) {
        console.error('Error starting session:', err);
        return null;
      }
    },
    [],
  );

  // Update last activity timestamp (optionally last_path)
  const updateActivity = useCallback(
    async (sessionId: string, path?: string) => {
      try {
        const patch: { last_activity_at: string; last_path?: string } = {
          last_activity_at: new Date().toISOString(),
        };
        if (path) patch.last_path = path;

        const { error } = await supabase
          .from('student_sessions')
          .update(patch)
          .eq('id', sessionId);

        if (error) {
          console.error('Error updating activity:', error);
        }
      } catch (err) {
        console.error('Error updating activity:', err);
      }
    },
    [],
  );

  // End the session
  const endSession = useCallback(async (sessionId: string | null) => {
    if (!sessionId) return;

    try {
      const { error } = await supabase
        .from('student_sessions')
        .update({
          ended_at: new Date().toISOString(),
        })
        .eq('id', sessionId)
        .is('ended_at', null);

      if (error) {
        console.error('Error ending session:', error);
      }
    } catch (err) {
      console.error('Error ending session:', err);
    }
  }, []);

  // Periodic activity update
  const scheduleActivityUpdate = useCallback(
    () => {
      const state = stateRef.current;

      if (state.activityTimer) {
        window.clearInterval(state.activityTimer);
      }

      state.activityTimer = window.setInterval(() => {
        if (state.sessionId) {
          void updateActivity(state.sessionId);
        }
      }, ACTIVITY_UPDATE_INTERVAL);
    },
    [updateActivity],
  );

  // Handle user activity (mouse/keyboard/etc)
  const handleActivity = useCallback(
    () => {
      const state = stateRef.current;
      state.lastActivityTime = Date.now();

      // Reset inactivity timer
      if (state.inactivityTimer) {
        window.clearTimeout(state.inactivityTimer);
      }

      state.inactivityTimer = window.setTimeout(() => {
        const current = stateRef.current;
        if (current.sessionId) {
          void endSession(current.sessionId);
          current.sessionId = null;
        }
      }, INACTIVITY_TIMEOUT);
    },
    [endSession],
  );

  // Initial session setup (on mount)
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      const studentUserId = await checkIsStudent();
      if (!studentUserId || cancelled) return;

      // Check if there's an active session (not ended)
      const { data: existingSession, error } = await supabase
        .from('student_sessions')
        .select('id')
        .eq('student_user_id', studentUserId)
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error loading existing student session:', error);
      }

      if (cancelled) return;

      if (existingSession) {
        // Resume existing session and bump activity + path
        stateRef.current.sessionId = existingSession.id;
        await updateActivity(existingSession.id, window.location.pathname);
      } else {
        // Start new session at current path
        const sessionId = await startSession(studentUserId, window.location.pathname);
        stateRef.current.sessionId = sessionId;
      }

      if (!cancelled && stateRef.current.sessionId) {
        scheduleActivityUpdate();
        handleActivity(); // sets up inactivity timer
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, checkIsStudent, startSession, updateActivity, scheduleActivityUpdate, handleActivity]);

  // Update last_path when location changes
  useEffect(() => {
    if (!enabled) return;
    const state = stateRef.current;
    if (state.sessionId) {
      void updateActivity(state.sessionId, location.pathname);
    }
  }, [enabled, location.pathname, updateActivity]);

  // Add/remove global activity listeners
  useEffect(() => {
    if (!enabled) return;
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, handleActivity]);

  // Handle page visibility changes
  useEffect(() => {
    if (!enabled) return;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden: end current session
        const state = stateRef.current;
        if (state.sessionId) {
          void endSession(state.sessionId);
          state.sessionId = null;
        }
      } else {
        // Page visible again: start a new session
        (async () => {
          const studentUserId = await checkIsStudent();
          if (studentUserId && !stateRef.current.sessionId) {
            const sessionId = await startSession(studentUserId, window.location.pathname);
            stateRef.current.sessionId = sessionId;
            if (sessionId) {
              scheduleActivityUpdate();
              handleActivity();
            }
          }
        })();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkIsStudent, startSession, scheduleActivityUpdate, handleActivity, endSession]);

  // Cleanup on unmount
  useEffect(() => {
    if (!enabled) return;
    // Capture the ref value at effect time, not cleanup time
    const state = stateRef.current;
    
    return () => {
      if (state.activityTimer) {
        window.clearInterval(state.activityTimer);
      }
      if (state.inactivityTimer) {
        window.clearTimeout(state.inactivityTimer);
      }

      if (state.sessionId) {
        void endSession(state.sessionId);
      }
    };
  }, [enabled, endSession]);

  // Handle page unload (best-effort)
  useEffect(() => {
    if (!enabled) return;
    const handleBeforeUnload = () => {
      const sessionId = stateRef.current.sessionId;
      if (sessionId) {
        void endSession(sessionId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, endSession]);
}
